import L from 'leaflet';

import throttle from 'lodash/throttle';

import { type ReactElement, useEffect, useReducer, useState } from 'react';
import { LeafletMap } from '../Map';
import { SidebarLayerControl } from '../Map/Controls';
import { BIKE_SECTION } from '../Map/Controls/SidebarLayerControl.types';
import {
    bikeFilterCurrent,
    bikeFilterPlanned,
    bikeParkingGbg,
    bikeRoadsGbg,
    bikeSituation,
    pumpStation,
    removeAllFilter,
    rentalBike,
} from '../Map/LayerFactories';
import { TrafikenConfig } from '../Map/TrafikenConfig';
import { MapSymbolsReference } from '../MapSymbolsReference';
import { logEvent } from '../Util';
import { VasttrafikGeocoder } from './VasttrafikGeocoder';
import { VasttrafikRouter } from './VasttrafikRouter';

import type { LayerSetupDefinition } from '../Map/Map.types';
import { useBootstrapModalBlur } from '../Util/useBootstrapModalBlur';
import type { BikePlannerGbgProps } from './BikePlannerGbg.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let Trafiken: any;

let routingControl;
let layers: { [key: string]: LayerSetupDefinition };

export const BikePlannerGbg = (props: BikePlannerGbgProps): ReactElement => {
    const mapProps = props.mapSettings;

    const [map, setMap] = useState<L.Map>();

    useBootstrapModalBlur('roadSymbolsModal');

    const setupMap = (map: L.Map): void => {
        const viewport = document.querySelector('meta[name=viewport]');
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');

        const region = mapProps.region.toLowerCase();
        const situationsLayer = bikeSituation(region);

        layers = {
            'bike-situations': {
                layer: situationsLayer,
                sections: [BIKE_SECTION],
            },
            'rental-bike': {
                layer: rentalBike(),
                sections: [BIKE_SECTION],
            },
            'pump-station': {
                layer: pumpStation(),
                sections: [BIKE_SECTION],
            },
            'bike-roads-gbg': {
                layer: bikeRoadsGbg(mapProps.mapServerUrl, mapProps.mapTilesVersion),
                sections: [BIKE_SECTION],
            },
            'bike-parkings-gbg': {
                layer: bikeParkingGbg(),
                sections: [BIKE_SECTION],
            },
            'clear-bike-situations': {
                layer: removeAllFilter([situationsLayer]),
                sections: [BIKE_SECTION],
            },
            'show-current-bike-situations': {
                layer: bikeFilterCurrent([situationsLayer]),
                sections: [BIKE_SECTION],
            },
            'show-planned-bike-situations': {
                layer: bikeFilterPlanned([situationsLayer]),
                sections: [BIKE_SECTION],
            },
        };

        const styrOchStallLayer = new Trafiken.StyrOchStall.Layer(TrafikenConfig.styrOchStall.serviceUrl, TrafikenConfig.gbgOpenApi.authKey);

        const geocoder = new VasttrafikGeocoder();
        const legacyGeocoder = geocoder.asLegacyGeocoder();

        const router = new VasttrafikRouter();

        routingControl = new Trafiken.RoutingControl({
            position: 'topleft',
            router: router,
            plan: new Trafiken.RoutePlan(
                undefined,
                {
                    geocoder: legacyGeocoder,
                    autocompleteOptions: { noResultsMessage: 'Inga sökresultat.' },
                    maxGeocoderTolerance: 10000,
                },
                map,
            ),
            lineOptions: {
                addWaypoints: false,
                styles: [
                    { color: 'black', opacity: 0.5, weight: 9 },
                    { color: 'white', opacity: 0.9, weight: 8 },
                    { color: 'purple', opacity: 1, weight: 3 },
                ],
            },
            altLineOptions: {
                styles: [
                    { color: 'black', opacity: 0.5, weight: 9 },
                    { color: 'white', opacity: 0.9, weight: 8 },
                    { color: '#a8a', opacity: 1, weight: 3 },
                ],
            },
            formatter: new Trafiken.Formatter({
                roundingSensitivity: 100,
            }),
            showAlternatives: true,
        }).addTo(map);
        styrOchStallLayer.addTo(map);
        Trafiken.BikePlannerPopup.geocoder = legacyGeocoder;
        Trafiken.BikePlannerPopup.routingControl = routingControl;

        L.DomEvent.on(routingControl, 'contextmenu', L.DomEvent.stopPropagation);
        map.on(
            'contextmenu',
            function (e: L.LeafletMouseEvent) {
                routingControl.openAddressPopup(e.latlng);
            },
            this,
        );

        map.on(
            'click',
            function () {
                if (Trafiken.BikePlannerPopup.currentPopup) {
                    map.closePopup();
                    Trafiken.BikePlannerPopup.clearPopup();
                }
            },
            this,
        );

        routingControl.on('waypointschanged', function (e) {
            const wps = e.waypoints;

            styrOchStallLayer.clear();
            if (wps.length > 0 && wps[0].latLng) {
                styrOchStallLayer.add(wps[0].latLng, TrafikenConfig.styrOchStall.radius, 'AvailableBikes', function (d) {
                    return d.value + (d.value !== 1 ? ' lediga cyklar' : ' ledig cykel');
                });
            }
            if (wps.length > 1 && wps[1].latLng) {
                styrOchStallLayer.add(wps[wps.length - 1].latLng, TrafikenConfig.styrOchStall.radius, 'AvailableBikeStands', function (d) {
                    return d.value + (d.value !== 1 ? ' lediga cykelställ' : ' ledigt cykelställ');
                });
            }

            logEvent('Bikeplanner', 'Waypoints changed', null, null);
        });

        setMap(map);
    };

    useEffect(() => {
        window.addEventListener('resize', onWindowResize);
        return () => {
            window.removeEventListener('resize', onWindowResize);
        };
    }, []);

    const [, forceUpdate] = useReducer((x) => x + 1, 0);

    const onWindowResize = (): void => {
        throttle(() => forceUpdate(), 500);
    };

    const onLocationMarkerClick = (e: L.LeafletMouseEvent): void => {
        routingControl.openAddressPopup(e.latlng, { description: 'Din ungefärliga position:' });
    };

    return (
        <>
            {map && (
                <SidebarLayerControl
                    map={map}
                    scope="bikeplanner-gbg"
                    region={mapProps.region.toLowerCase()}
                    layers={layers}
                    persistLayersInUrl={true}
                    defaultOnLayers={['bike-situations', 'clear-bike-situations', 'show-current-bike-situations', 'show-planned-bike-situations']}
                    sectionsToShow={[BIKE_SECTION]}
                    defaultSection={BIKE_SECTION}
                    sectionHeaders={{ [BIKE_SECTION]: 'Visa i karta' }}
                    roadHelpElementId="roadSymbolsModal"
                />
            )}
            <LeafletMap
                {...mapProps}
                disableFullscreenControl={true}
                showMyPosition={true}
                scrollWheelZoom={true}
                onLocationMarkerClick={onLocationMarkerClick}
                onMapInitiated={setupMap}
            />
            <div className="modal fade" id="roadSymbolsModal" tabIndex={-1} role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title" id="exampleModalLabel">
                                Teckenförklaring
                            </h2>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <MapSymbolsReference region={mapProps.region.toLowerCase()} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
